import React from 'react';
import {GoogleOAuthProvider} from '@react-oauth/google';
import {BrowserRouter as Router, Routes, Route, Navigate} from 'react-router-dom';
import {Provider, useDispatch, useSelector} from 'react-redux';
import {store, RootState} from './store';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import Home from './pages/main/Home';
import DiscoverTalent from './pages/main/DiscoverTalent';
import Profile from './pages/main/Profile';
import Settings from './pages/main/Settings';
import Services from './pages/main/Services';
import Advertise from './pages/main/Advertise';
import Analytics from './pages/main/Analytics';
import Messages from './pages/main/Messages';
import Notifications from './pages/main/Notifications';
import BillingRedirectPage from './pages/main/BillingRedirectPage';
import {PrivacyPolicyPage, TermsOfServicePage} from './pages/legal/LegalDocumentPage';
import {
  AboutPage,
  AccountDeletionPage,
  ChildSafetyStandardsPage,
  ContactPage,
} from './pages/public/PublicInfoPage';
import Layout from './components/Layout';
import PrivateRoute from './components/PrivateRoute';
import AppLoader from './components/AppLoader';
import {logout} from './store/slices/authSlice';
import {clearNotificationState} from './store/slices/notificationSlice';
import {clearUser, setUser} from './store/slices/userSlice';
import {authApi} from './services/authApi';
import {RealtimeProvider} from './realtime/RealtimeProvider';
import './App.css';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID?.trim() || '';

const PublicAuthPage: React.FC = () => {
  const token = useSelector((state: RootState) => state.auth.token);

  return token ? <Navigate to="/home" replace /> : <Login />;
};

const AppRoutes: React.FC = () => {
  const dispatch = useDispatch();
  const token = useSelector((state: RootState) => state.auth.token);
  const user = useSelector((state: RootState) => state.user.currentUser);
  const [authChecked, setAuthChecked] = React.useState(!token);

  React.useEffect(() => {
    let isMounted = true;

    const restoreSession = async () => {
      if (!token) {
        if (user) {
          dispatch(clearUser());
        }

        dispatch(clearNotificationState());

        if (isMounted) {
          setAuthChecked(true);
        }
        return;
      }

      if (user) {
        if (isMounted) {
          setAuthChecked(true);
        }
        return;
      }

      if (isMounted) {
        setAuthChecked(false);
      }

      try {
        const currentUser = await authApi.getCurrentUser(token);

        if (!isMounted) {
          return;
        }

        dispatch(setUser(currentUser));
      } catch (error) {
        if (!isMounted) {
          return;
        }

        dispatch(logout());
        dispatch(clearUser());
        dispatch(clearNotificationState());
      } finally {
        if (isMounted) {
          setAuthChecked(true);
        }
      }
    };

    void restoreSession();

    return () => {
      isMounted = false;
    };
  }, [dispatch, token, user]);

  if (!authChecked) {
    return <AppLoader message="Loading your workspace..." />;
  }

  return (
    <Routes>
      <Route path="/" element={<PublicAuthPage />} />
      <Route path="/login" element={<PublicAuthPage />} />
      <Route path="/auth/facebook/callback" element={<PublicAuthPage />} />
      <Route path="/auth/twitter/callback" element={<PublicAuthPage />} />
      <Route path="/auth/tiktok/callback" element={<PublicAuthPage />} />
      <Route path="/register" element={<Register />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/policy" element={<Navigate to="/privacy" replace />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/account-deletion" element={<AccountDeletionPage />} />
      <Route path="/child-safety-standards" element={<ChildSafetyStandardsPage />} />
      <Route path="/abuse" element={<Navigate to="/contact" replace />} />
      <Route path="/billing/success" element={<BillingRedirectPage />} />
      <Route path="/billing/cancel" element={<BillingRedirectPage />} />
      
      <Route element={<PrivateRoute />}>
        <Route element={<RealtimeProvider><Layout /></RealtimeProvider>}>
          <Route path="/home" element={<Home />} />
          <Route path="/discover-talent" element={<DiscoverTalent />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/messages" element={<Messages />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/services" element={<Services />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/advertise" element={<Advertise />} />
          <Route path="/settings" element={<Settings />} />
        </Route>
      </Route>
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  const appContent = (
    <Provider store={store}>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <div className="app-shell">
          <AppRoutes />
        </div>
      </Router>
    </Provider>
  );

  if (!GOOGLE_CLIENT_ID) {
    return appContent;
  }

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      {appContent}
    </GoogleOAuthProvider>
  );
}

export default App;
